import { z } from 'zod'
import type { ProductCategoryId } from '../constants'
import { CONTACT_MASK, filterDirectContact } from '../contact-filter'
import type { FactorySpecSheet } from '../spec-sheet'

/**
 * AI 중계 샘플·시제품 거래 (2026-10-30 대표 지시). 설계는 docs/design-ai-brokered-samples.md.
 * 공장마다 샘플 방식이 달라 공장이 방식별 제안(sample offer)을 내고, 고객이 고르면 결제 뒤에 서로를 공개한다.
 */

/**
 * 샘플 방식 — 6종 확정(2026-10-30 대표 결정 2차 C). 평가(시식)·분석(이벤트 offerType)·관리자 화면이 모두 이 키를 쓴다.
 * 저장값이라 키를 바꾸지 않는다. free_sample ① 무료 샘플 · paid_prototype ② 유료 시제품(개발비) · info_for_sample ③ 고객
 * 정보를 받아야 샘플을 주는 곳 · free_tasting ④ 무료로 개발해 맛보게 하는 곳(시식) · paid_consulting ⑤ 유료 시제품 컨설팅 ·
 * portion_kit 소분 키트(완제품·원료를 작게 나눠 파는 유료 키트)
 */
export const SAMPLE_OFFER_TYPES = [
  'free_sample',
  'paid_prototype',
  'info_for_sample',
  'free_tasting',
  'paid_consulting',
  'portion_kit',
] as const
export type SampleOfferType = (typeof SAMPLE_OFFER_TYPES)[number]

/** 키 하나가 샘플 방식인지 (앱 경로 파라미터·분석 값 검사) */
export function isSampleOfferType(value: unknown): value is SampleOfferType {
  return (SAMPLE_OFFER_TYPES as readonly unknown[]).includes(value)
}

/**
 * 무료 유형 (대표 결정 2차 B). 기본값이 아니라 공장이 무료로 제안할 때만 쓰는 예외이고, 그때는 비용 부담자와 예산(금액·수량
 * 한도)을 terms.freeCost 에 남긴다. 관리자·공장 포털의 새 제안 기본 유형은 DEFAULT_SAMPLE_OFFER_TYPE(유료 시제품)이다
 */
export const SAMPLE_FREE_OFFER_TYPES = [
  'free_sample',
  'free_tasting',
] as const satisfies readonly SampleOfferType[]
export function sampleOfferIsFree(type: SampleOfferType): boolean {
  return (SAMPLE_FREE_OFFER_TYPES as readonly SampleOfferType[]).includes(type)
}
export const DEFAULT_SAMPLE_OFFER_TYPE: SampleOfferType = 'paid_prototype'

/** 무료 제안의 비용을 누가 내는지. 공장 · 레시피캣 · 나눠 냄 */
export const SAMPLE_COST_BEARERS = ['factory', 'recipecat', 'shared'] as const
export type SampleCostBearer = (typeof SAMPLE_COST_BEARERS)[number]

/** 공장 가격을 받는 유형. 나머지는 '결제 뒤 공개' 원칙대로 관리자 연결 수수료를 받는다 */
export function sampleOfferChargesPrice(type: SampleOfferType): boolean {
  return type === 'paid_prototype' || type === 'paid_consulting' || type === 'portion_kit'
}

/** 고객이 공장에 넘길 수 있는 정보 항목. 결제 뒤 항목마다 동의해야 공장에 보인다 (개인정보 제3자 제공) */
export const CUSTOMER_INFO_ITEMS = [
  'name',
  'phone',
  'email',
  'address',
  'company',
  'business_number',
] as const
export type CustomerInfoItem = (typeof CUSTOMER_INFO_ITEMS)[number]

export const SAMPLE_CURRENCIES = ['KRW', 'USD'] as const
export type SampleCurrency = (typeof SAMPLE_CURRENCIES)[number]

const shortText = (max: number) => z.string().trim().max(max).default('')

/** 무료 제안의 비용 부담과 예산. 금액(terms.currency)이나 수량 한도 중 하나는 있어야 한다. 고객에게는 보이지 않는다 */
export const sampleFreeCostSchema = z
  .object({
    bearer: z.enum(SAMPLE_COST_BEARERS),
    /** 이 제안으로 무료 제공에 쓸 수 있는 총액 (terms.currency) */
    budgetAmount: z.number().positive().max(1_000_000_000).nullable().default(null),
    /** 무료로 내줄 수 있는 최대 건수 */
    budgetQuantity: z.number().int().positive().max(100_000).nullable().default(null),
    note: shortText(300),
  })
  .strict()
export type SampleFreeCost = z.infer<typeof sampleFreeCostSchema>

/** 제안 조건. 유형에 맞지 않는 칸은 비워 둔다 (시식 지역은 시식만, 컨설팅 시간은 컨설팅만) */
export const sampleOfferTermsSchema = z
  .object({
    currency: z.enum(SAMPLE_CURRENCIES).default('KRW'),
    /** 공장 가격(②⑤). 무료 유형은 null — 고객은 연결 수수료를 낸다 */
    price: z.number().positive().max(100_000_000).nullable().default(null),
    /** 결제 뒤 발송·일정 확정까지 걸리는 일수. 넘으면 관리자 경보 */
    leadDays: z.number().int().min(1).max(120),
    /** 본 발주 때 이 금액을 빼 주는지 (②) */
    creditedToOrder: z.boolean().default(false),
    /** 결제 뒤 고객이 동의해야 넘어가는 정보 항목 */
    requiredInfo: z.array(z.enum(CUSTOMER_INFO_ITEMS)).max(CUSTOMER_INFO_ITEMS.length).default([]),
    /** 시식 가능 지역 (④). 고객에게 그대로 보인다 */
    tastingRegions: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
    /** 시식 가능 요일 0(일)~6(토) (④) */
    tastingWeekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    /** 컨설팅 시간(분) (⑤) */
    consultingMinutes: z.number().int().min(10).max(6000).nullable().default(null),
    /** 받는 것: 샘플 수량·용량, 컨설팅 결과물 등 */
    deliverables: shortText(500),
    note: shortText(1000),
    /** 무료 유형(SAMPLE_FREE_OFFER_TYPES)만. 비용 부담자·예산 (대표 결정 2차 B). 옛 제안은 칸이 없다 */
    freeCost: sampleFreeCostSchema.nullable().default(null),
  })
  .strict()
export type SampleOfferTerms = z.infer<typeof sampleOfferTermsSchema>

/** 유형과 조건이 맞는지. 맞으면 null, 아니면 어긋난 칸 이름 */
export function sampleOfferTermsProblem(
  type: SampleOfferType,
  terms: SampleOfferTerms,
): string | null {
  if (sampleOfferChargesPrice(type) !== (terms.price !== null)) return 'price'
  if (type === 'info_for_sample' && terms.requiredInfo.length === 0) return 'requiredInfo'
  if (type !== 'free_tasting' && (terms.tastingRegions.length || terms.tastingWeekdays.length))
    return 'tasting'
  if ((type === 'paid_consulting') !== (terms.consultingMinutes !== null))
    return 'consultingMinutes'
  const freeCost = terms.freeCost ?? null
  if (sampleOfferIsFree(type) !== (freeCost !== null)) return 'freeCost'
  if (freeCost && freeCost.budgetAmount === null && freeCost.budgetQuantity === null)
    return 'freeCost'
  return null
}

/** 법인 꼴 말 — 공장 이름을 가릴 때 이것을 뗀 이름도 가린다 ('한빛식품 주식회사' → '한빛식품') */
const CORPORATE_AFFIXES =
  /\(주\)|㈜|주식회사|유한회사|농업회사법인|영농조합법인|株式会社|有限公司|股份有限公司|\bco\.?,?\s*ltd\.?|\binc\.?|\bcorp\.?|\bllc\b/giu

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** 글 안의 공장 이름(법인 꼴 말을 뗀 이름 포함)을 가린다. 두 글자보다 짧은 이름은 흔한 말까지 지워 가리지 않는다 */
function maskFactoryName(text: string, factoryName: string): string {
  const names = [factoryName, factoryName.replace(CORPORATE_AFFIXES, '')]
    .map((n) => n.normalize('NFKC').replace(/\s+/g, ' ').trim())
    .filter((n, i, all) => n.length >= 2 && all.indexOf(n) === i)
    .sort((a, b) => b.length - a.length)
  return names.reduce(
    (out, name) =>
      out.replace(new RegExp(escapeRegExp(name).replace(/ /g, '\\s*'), 'giu'), CONTACT_MASK),
    text,
  )
}

/**
 * 고객에게 보이는 조건 (대표 결정 2차 E: 결제 전 공장 익명). 공장이 적은 글(받는 것·메모·시식 지역)에서 연락처·링크·계좌·
 * 메신저 아이디를 가리고, 결제 전이면(factoryNameToHide) 공장 이름도 가린다. 무료 비용 부담·예산(freeCost)은 내부 칸이라 뺀다.
 * 고객 경로(제안 목록·주문 화면)는 이 함수 하나를 거친다. 주소는 패턴으로 확실히 잡을 수 없어 입력 화면에서 쓰지 말라고 안내한다
 */
export function customerSampleTerms(
  terms: SampleOfferTerms,
  factoryNameToHide: string | null,
): SampleOfferTerms {
  const clean = (text: string) => {
    const masked = filterDirectContact(text).text
    return factoryNameToHide ? maskFactoryName(masked, factoryNameToHide) : masked
  }
  return {
    ...terms,
    deliverables: clean(terms.deliverables),
    note: clean(terms.note),
    tastingRegions: terms.tastingRegions.map(clean),
    freeCost: null,
  }
}

export const SAMPLE_OFFER_STATUSES = ['draft', 'active', 'paused'] as const
export type SampleOfferStatus = (typeof SAMPLE_OFFER_STATUSES)[number]

/** 공장 이름·넓은 지역·가능 분류는 제안에 적지 않는다 — 공장 목록(factories)에서 읽는다 (2026-10-30 '공장 정보 하나로') */
const offerFields = {
  offerType: z.enum(SAMPLE_OFFER_TYPES),
  status: z.enum(SAMPLE_OFFER_STATUSES),
  terms: sampleOfferTermsSchema,
}
const termsMatchType = (o: { offerType: SampleOfferType; terms: SampleOfferTerms }) =>
  sampleOfferTermsProblem(o.offerType, o.terms) === null

/** 공장 포털의 제안 저장. 공장은 로그인한 공장 계정의 것만 고친다 */
export const factorySampleOfferInputSchema = z
  .object(offerFields)
  .strict()
  .refine(termsMatchType, { message: 'Terms do not match the sample type', path: ['terms'] })
export type FactorySampleOfferInput = z.infer<typeof factorySampleOfferInputSchema>

/** 관리자의 제안 저장. 공장 목록(factories.id)에서 공장을 고른다 (docs/design-ai-brokered-samples.md 2절) */
export const adminSampleOfferInputSchema = z
  .object({ ...offerFields, factoryId: z.string().trim().min(1).max(100) })
  .strict()
  .refine(termsMatchType, { message: 'Terms do not match the sample type', path: ['terms'] })
export type AdminSampleOfferInput = z.infer<typeof adminSampleOfferInputSchema>

/**
 * 결제 상태와 업무 상태 (대표 결정 2차 D: 결제와 업무를 한 칸에 합치지 않는다). sample_orders.payment_status ·
 * fulfilment_status 두 칸이 원본이고, 서버의 상태 판단은 모두 이 두 칸으로 한다.
 * - 결제: unpaid(결제 전) → paid(결제 확인, 서로 공개) → refund_pending(관리자 환불 검토) → refunded(환불 확정 이벤트 뒤)
 * - 업무: awaiting_payment → awaiting_info(결제 뒤 고객 동의 대기) → spec_sent(공장이 사양·정보를 받음)
 *   → in_progress(공장이 발송·일정 기록) → delivered(고객 수령·참석) → completed(시식 기록). cancelled 는 결제 전 취소.
 *   환불은 결제 칸만 바꾸고 업무 칸은 그때까지 한 일을 그대로 남긴다
 */
export const SAMPLE_PAYMENT_STATUSES = ['unpaid', 'paid', 'refund_pending', 'refunded'] as const
export type SamplePaymentStatus = (typeof SAMPLE_PAYMENT_STATUSES)[number]
export const SAMPLE_WORK_STATUSES = [
  'awaiting_payment',
  'awaiting_info',
  'spec_sent',
  'in_progress',
  'delivered',
  'completed',
  'cancelled',
] as const
export type SampleWorkStatus = (typeof SAMPLE_WORK_STATUSES)[number]

/**
 * 예전 한 칸 상태(sample_orders.status). 화면 표시·관리자 거르기용으로 남아 있고, 두 칸에서 sampleOrderLegacyStatus 로 만든다.
 * 새 판단에 쓰지 않는다
 */
export const SAMPLE_ORDER_STATUSES = [
  'pending_payment',
  'paid',
  'spec_sent',
  'in_progress',
  'delivered',
  'completed',
  'cancelled',
  'refund_pending',
  'refunded',
] as const
export type SampleOrderStatus = (typeof SAMPLE_ORDER_STATUSES)[number]

/**
 * 예전 한 칸 상태 → 두 칸 대응표. 마이그레이션(0059)이 같은 표로 옛 행을 옮겼다. 환불 행은 그때까지 한 일을 알 수 없어
 * 공장 진행 기록(fulfilment)이 있으면 in_progress, 없으면 awaiting_info 로 옮겼다(운영 적용 전이라 개발 데이터만 해당)
 */
export const SAMPLE_ORDER_STATUS_SPLIT: Record<
  Exclude<SampleOrderStatus, 'refund_pending' | 'refunded'>,
  { payment: SamplePaymentStatus; work: SampleWorkStatus }
> = {
  pending_payment: { payment: 'unpaid', work: 'awaiting_payment' },
  cancelled: { payment: 'unpaid', work: 'cancelled' },
  paid: { payment: 'paid', work: 'awaiting_info' },
  spec_sent: { payment: 'paid', work: 'spec_sent' },
  in_progress: { payment: 'paid', work: 'in_progress' },
  delivered: { payment: 'paid', work: 'delivered' },
  completed: { payment: 'paid', work: 'completed' },
}

/** 두 칸 → 예전 한 칸 상태. 결제가 paid 가 아니면 결제 쪽이, paid 면 업무 쪽이 이름이 된다 */
export function sampleOrderLegacyStatus(
  payment: SamplePaymentStatus,
  work: SampleWorkStatus,
): SampleOrderStatus {
  if (payment === 'refund_pending' || payment === 'refunded') return payment
  if (payment === 'unpaid') return work === 'cancelled' ? 'cancelled' : 'pending_payment'
  if (work === 'awaiting_payment' || work === 'awaiting_info' || work === 'cancelled') return 'paid'
  return work
}

/** 상태를 바꾸는 UPDATE 의 값 — 두 칸과 예전 한 칸을 함께 쓴다 */
export function sampleOrderStatusColumns(payment: SamplePaymentStatus, work: SampleWorkStatus) {
  return {
    paymentStatus: payment,
    fulfilmentStatus: work,
    status: sampleOrderLegacyStatus(payment, work),
  }
}

/** 환불 쪽 UPDATE 의 값 — 결제 칸만 바꾸고 업무 칸(그때까지 한 일)은 그대로 둔다. 예전 한 칸은 결제 쪽 이름이다 */
export function sampleRefundStatusColumns(payment: 'refund_pending' | 'refunded') {
  return { paymentStatus: payment, status: payment }
}

/** 결제가 확인돼 서로 공개된 상태 (환불 검토 중까지. 환불이 확정되면 다시 닫는다) */
export function sampleOrderDisclosed(payment: SamplePaymentStatus): boolean {
  return payment === 'paid' || payment === 'refund_pending'
}

/** 공장이 아직 발송·일정을 기록하지 않은 업무 상태 — 결제가 paid 일 때 기한 초과 경보 대상 */
export const SAMPLE_ORDER_AWAITING_FACTORY = [
  'awaiting_info',
  'spec_sent',
] as const satisfies readonly SampleWorkStatus[]

/** 시식을 남길 수 있는 업무 상태 — 고객이 받은 뒤만 (미수령 시식 금지, 구현현황표 N) */
export const SAMPLE_TASTABLE_WORK_STATUSES = [
  'delivered',
  'completed',
] as const satisfies readonly SampleWorkStatus[]

/** 공장 진행 기록의 종류. 유형에서 정해진다 */
export const SAMPLE_FULFILMENT_KINDS = [
  'shipped',
  'tasting_scheduled',
  'consulting_scheduled',
] as const
export type SampleFulfilmentKind = (typeof SAMPLE_FULFILMENT_KINDS)[number]
export function sampleFulfilmentKind(type: SampleOfferType): SampleFulfilmentKind {
  if (type === 'free_tasting') return 'tasting_scheduled'
  if (type === 'paid_consulting') return 'consulting_scheduled'
  return 'shipped'
}

/** 고객이 공장에 바라는 것 (익명 브리프). 결제 전에도 공장에 보이므로 연락처를 넣지 말라고 화면에서 안내한다 */
export const sampleBriefSchema = z.object({
  request: z.string().trim().max(1000).default(''),
})
export type SampleBrief = z.infer<typeof sampleBriefSchema>

/** POST /sample-orders */
export const createSampleOrderSchema = z.object({
  offerId: z.string().min(1).max(64),
  simulationId: z.string().min(1).max(64),
  brief: sampleBriefSchema.prefault({}),
})
export type CreateSampleOrderInput = z.infer<typeof createSampleOrderSchema>

/** GET /sample-orders/offers?simulationId= */
export const sampleOffersQuerySchema = z.object({ simulationId: z.string().min(1).max(64) })

/** PUT /sample-orders/:id/disclosures — 항목마다 값과 동의. 한 번에 여러 항목 */
export const sampleDisclosureInputSchema = z.object({
  items: z
    .array(
      z.object({
        item: z.enum(CUSTOMER_INFO_ITEMS),
        value: z.string().trim().min(1).max(300),
        consent: z.literal(true),
      }),
    )
    .min(1)
    .max(CUSTOMER_INFO_ITEMS.length),
})
export type SampleDisclosureInput = z.infer<typeof sampleDisclosureInputSchema>

/** POST /sample-orders/:id/disclosures/withdraw — 동의 철회. 값을 지우고 공장 화면에서 사라진다 */
export const sampleDisclosureWithdrawSchema = z.object({ item: z.enum(CUSTOMER_INFO_ITEMS) })

/** 공장(또는 관리자)의 진행 기록 — 발송했거나 시식·컨설팅 일정을 잡았다 */
export const sampleProgressInputSchema = z.object({
  note: z.string().trim().min(1).max(500),
  /** 시식·컨설팅 일시 (발송은 비움) */
  scheduledAt: z.iso.datetime({ offset: true }).nullable().default(null),
})
export type SampleProgressInput = z.infer<typeof sampleProgressInputSchema>

/** 관리자 결정: 환불 검토로 돌리기(refund_review), 공급자에 환불 요청(refund), 기한 연장(extend) */
export const adminSampleOrderActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('refund_review'), note: z.string().trim().min(1).max(500) }),
  z.object({ action: z.literal('refund'), note: z.string().trim().min(1).max(500) }),
  z.object({
    action: z.literal('extend'),
    days: z.number().int().min(1).max(60),
    note: z.string().trim().min(1).max(500),
  }),
])
export type AdminSampleOrderAction = z.infer<typeof adminSampleOrderActionSchema>

// ── 응답 ────────────────────────────────────────────────────────────────

/** 결제 전 고객이 보는 제안. 공장 이름·식별자가 없다 */
export interface SampleOfferPublicView {
  id: string
  /** 익명 이름 재료 (앱이 sampleFactoryLabel 로 앱 언어 이름을 만든다) */
  anon: SampleFactoryAnon
  offerType: SampleOfferType
  terms: SampleOfferTerms
  /** 고객이 낼 금액. 무료 유형은 연결 수수료 (연결 수수료가 비어 고를 수 없는 제안은 목록에 없다) */
  amount: number
  currency: SampleCurrency
}

export interface SampleFactoryAnon {
  region: string
  categoryId: ProductCategoryId
  /** 공장 순번 글자 (A, B, …) */
  letter: string
}

/** 고객이 동의한 항목 (값은 본인에게만 보인다) */
export interface SampleDisclosureView {
  item: CustomerInfoItem
  value: string | null
  consentedAt: string | null
  withdrawnAt: string | null
}

export interface SampleFulfilment {
  kind: SampleFulfilmentKind
  note: string
  scheduledAt: string | null
  recordedAt: string
}

/** 고객의 주문 화면 */
export interface SampleOrderView {
  id: string
  simulationId: string
  offerType: SampleOfferType
  terms: SampleOfferTerms
  anon: SampleFactoryAnon
  /** 결제 뒤에만 */
  factoryName: string | null
  /** 예전 한 칸 상태 (표시용, sampleOrderLegacyStatus) */
  status: SampleOrderStatus
  paymentStatus: SamplePaymentStatus
  fulfilmentStatus: SampleWorkStatus
  amount: number
  currency: SampleCurrency
  brief: SampleBrief
  paidAt: string | null
  dueAt: string | null
  fulfilment: SampleFulfilment | null
  disclosures: SampleDisclosureView[]
  /** 아직 동의하지 않은 필수 항목 */
  missingInfo: CustomerInfoItem[]
  /** 개발용 가짜 결제를 쓸 수 있는 서버인지 (결제 대기 중일 때만 의미) */
  devPayment: boolean
  createdAt: string
}

/** 공장 포털의 주문. 결제 전에는 익명 브리프만, 결제 뒤에는 사양서와 동의한 항목 */
export interface FactorySampleOrderView {
  id: string
  /** '고객 #' 뒤에 붙는 짧은 번호 */
  customerRef: string
  offerType: SampleOfferType
  terms: SampleOfferTerms
  /** 예전 한 칸 상태 (표시용, sampleOrderLegacyStatus) */
  status: SampleOrderStatus
  paymentStatus: SamplePaymentStatus
  fulfilmentStatus: SampleWorkStatus
  amount: number
  currency: SampleCurrency
  categoryId: ProductCategoryId | null
  brief: SampleBrief
  paidAt: string | null
  dueAt: string | null
  fulfilment: SampleFulfilment | null
  /** 동의가 살아 있는 항목만 */
  disclosed: Array<{ item: CustomerInfoItem; value: string }>
  spec: FactorySpecSheet | null
  createdAt: string
}

/** 제안을 낼 수 있는 공장 한 곳 (GET /admin/sample-factories). 공장 목록(factories)에서 */
export interface SampleFactoryOption {
  id: string
  name: string
  region: string
  /** 공장 포털 계정과 이어졌는지 (factories.manufacturing_entity_id). 없으면 관리자만 제안을 고친다 */
  portalLinked: boolean
}

/** 제안 한 건. factoryName·anonRegion·categoryIds 는 공장 목록에서 읽은 값이다 */
export interface SampleOfferView {
  id: string
  factoryId: string
  factoryName: string
  anonRegion: string
  categoryIds: ProductCategoryId[]
  offerType: SampleOfferType
  status: SampleOfferStatus
  terms: SampleOfferTerms
  updatedAt: string
}

/** 관리자 주문 목록 1행 */
export interface AdminSampleOrderView {
  id: string
  userId: string
  simulationId: string
  offerId: string
  factoryId: string
  factoryName: string
  offerType: SampleOfferType
  /** 예전 한 칸 상태 (표시용, sampleOrderLegacyStatus) */
  status: SampleOrderStatus
  paymentStatus: SamplePaymentStatus
  fulfilmentStatus: SampleWorkStatus
  amount: number
  currency: SampleCurrency
  provider: string | null
  paidAt: string | null
  dueAt: string | null
  overdueAt: string | null
  fulfilment: SampleFulfilment | null
  missingInfo: CustomerInfoItem[]
  createdAt: string
}

/** 주문 감사 기록의 종류 */
export const SAMPLE_ORDER_EVENT_KINDS = [
  'created',
  'checkout_started',
  'paid',
  'consented',
  'withdrawn',
  'spec_sent',
  'progress',
  'received',
  'completed',
  'cancelled',
  'overdue',
  'refund_review',
  'refund_requested',
  'refunded',
  'refund_needs_review',
  'extended',
] as const
export type SampleOrderEventKind = (typeof SAMPLE_ORDER_EVENT_KINDS)[number]
